'use client';

import type { ReactNode, Ref } from 'react';
import { Button, TextButton } from '@galapagos/design-system/web';
import { useTranslation } from 'react-i18next';
import { FormActions } from '../../components/FormActions';
import { PageTitle } from '../../components/PageTitle';
import { SignupShell } from '../../components/SignupShell';
import { StepStatusRegion } from '../../components/StepStatusRegion';
import { ensureSignupStepStyle } from '../../components/signup-step-style';
import { ensureBaselineStyle } from '../../baseline/baseline-style';
import type { BaselineRead, BaselineSave } from '../../baseline/useBaselineStep';
import { useSignupNavigation } from '../../hooks/useSignupNavigation';
import type { SignupStepId } from '../../steps';
import { ensureWrapUpStyle } from '../../wrap-up/wrap-up-style';

export interface BaselineFrameProps {
  title: string;
  description?: string;
  headingRef: Ref<HTMLHeadingElement>;
  /** What the step announces now; empty when nothing. */
  status: string;
  statusTone?: 'neutral' | 'error';
  /** The step's content: the pickers or the summary, or nothing while it loads. */
  body?: ReactNode;
  /**
   * The step's actions bar. It sits after the group, directly in the step
   * column, so on a phone it can stick to the bottom of the screen.
   */
  children?: ReactNode;
}

/**
 * The frame of a last-week step, rendered once for the life of the step: the
 * heading keeps its focus and the one polite live region (ADR-025) stays
 * mounted while the step moves from loading to its form, so only the body,
 * the status text and the buttons change.
 */
export function BaselineFrame({
  title,
  description,
  headingRef,
  status,
  statusTone = 'neutral',
  body,
  children,
}: BaselineFrameProps) {
  ensureSignupStepStyle();
  // The shared step status and private-subtree rules.
  ensureWrapUpStyle();
  ensureBaselineStyle();
  return (
    <SignupShell>
      <div className="signup-step">
        <PageTitle title={title} description={description} headingRef={headingRef} />
        <section className="signup-step__group" aria-label={title} data-testid="signup-step">
          {body ?? null}
          <StepStatusRegion message={status} tone={statusTone} />
        </section>
        {children}
      </div>
    </SignupShell>
  );
}

/** The step's status text: a read in flight or failed, then a save in flight or failed. */
export function useBaselineStatus(
  read: BaselineRead<unknown>,
  save?: BaselineSave,
): { status: string; statusTone: 'neutral' | 'error' } {
  const { t } = useTranslation('signup');
  if (read.status === 'loading') return { status: t('baseline.loading'), statusTone: 'neutral' };
  if (read.status === 'error') return { status: t('baseline.loadError'), statusTone: 'error' };
  if (save?.pending) return { status: t('baseline.saving'), statusTone: 'neutral' };
  if (save?.failed) return { status: t('baseline.saveError'), statusTone: 'error' };
  return { status: '', statusTone: 'neutral' };
}

/**
 * The buttons of a step whose reads have not landed: none while loading; a
 * Continue that passes through without saving when the content has no
 * backend yet; a retry after any other failure.
 */
export function BaselineFallbackActions({
  read,
  passThroughTo,
}: {
  read: Exclude<BaselineRead<unknown>, { status: 'ready' }>;
  passThroughTo: SignupStepId;
}) {
  const { t } = useTranslation('signup');
  const { goToStep } = useSignupNavigation();
  if (read.status === 'loading') return null;
  if (read.status === 'unavailable') {
    return (
      <FormActions>
        <Button
          size="L"
          type="Primary"
          label={t('actions.continue')}
          onPress={() => goToStep(passThroughTo)}
          actionId="signup.baseline-pass-through.activate"
          data-testid="signup-baseline-pass-through-button"
        />
      </FormActions>
    );
  }
  return (
    <FormActions>
      <TextButton
        label={t('baseline.retry')}
        actionId="signup.baseline-retry.activate"
        data-testid="signup-baseline-retry-button"
        onPress={read.retry}
      />
    </FormActions>
  );
}
